import { GenericDialog } from "src/components";
import { connect } from "react-redux";
import { addEditConfigurationDialogActions } from "./add-edit-configuration-dialog-store";
import { configuratorPageK, useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { Button, TooltipShortcut } from "@itk/mui-components";
import { ProductDetails, SelectProduct, SelectSystem } from "./components";
import { RootState } from "src/store";
import { useMemo, useState } from "react";
import { TRenderStep } from "./add-edit-configuration-dialog-types";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { createGuid } from "@itk/typescript-utils";
import { useNavigate, useParams } from "react-router";
import { configureProductActions } from "src/pages/configure-product/configure-product-store";
import { ROUTES_URL } from "src/utils/constants";
import { TConfigureProductSearchParams } from "src/pages/configure-product/configure-product-types";

const mapStateToProps = (state: RootState) => ({
  addEditConfigurationDialog: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.addEditConfigurationDialog,
  formData: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.formData
});

const ownActions = {
  getConfigurationRequest: configureProductActions.getConfigurationRequest,
  setSelectedProjectLineItemId: configureProductActions.setSelectedProjectLineItemId,
  setAddEditConfigurationDialog: addEditConfigurationDialogActions.setAddEditConfigurationDialog,
  createProjectLineItemRequest: addEditConfigurationDialogActions.createProjectLineItemRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const AddEditConfigurationDialogBase = (props: Props) => {
  const { addEditConfigurationDialog, setAddEditConfigurationDialog, formData, createProjectLineItemRequest, getConfigurationRequest, setSelectedProjectLineItemId } = props;

  const navigate = useNavigate();

  const { projectId = "", configurationId = "" } = useParams<{ projectId: string; configurationId: string }>();

  const [isLoading, setIsLoading] = useState(false);

  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  const handleSave = (productId?: string) => {
    if (!formData.productsFilter || !formData.productsFilter.systemPackageId) return;
    setIsLoading(true);
    const projectLineItemId = createGuid();
    createProjectLineItemRequest(
      {
        projectLineItemId,
        parentProjectLineItemId: formData?.productsFilter?.parentProjectLineItemId || null,
        systemPackageId: formData.productsFilter.systemPackageId,
        productId: productId ?? formData.selectedProductId,
        projectId
      },
      (isSuccess, data) => {
        setIsLoading(false);
        if (isSuccess && data) {
          // if it has configuration id that means the dialog was opened on configure-product page
          if (configurationId) {
            getConfigurationRequest(configurationId);
            setSelectedProjectLineItemId(projectLineItemId);
          } else {
            navigate({
              pathname: ROUTES_URL.CONFIGURE_PRODUCT.build(data.projectId, data.configurationId),
              search: data.configurationId !== projectLineItemId ? `?${new URLSearchParams({ projectLineItemId } as TConfigureProductSearchParams).toString()}` : ""
            });
          }
          setAddEditConfigurationDialog(undefined);
        }
      }
    );
  };

  const renderStep = useMemo((): TRenderStep => {
    switch (addEditConfigurationDialog?.step) {
      case "SELECT SYSTEM":
        return {
          dialogTitle: configuratorPageT.selectSystem,
          component: <SelectSystem onSave={() => setAddEditConfigurationDialog({ step: "SELECT PRODUCT" })} />,
          saveAndCancelButton: {
            saveDisabled: !formData.productsFilter?.systemPackageId,
            saveLabel: "selectSystem",
            saveFn() {
              setAddEditConfigurationDialog({ step: "SELECT PRODUCT" });
            },
            cancelLabel: "cancel",
            cancelFn() {
              setAddEditConfigurationDialog(undefined);
            }
          }
        };
      case "SELECT PRODUCT":
        return {
          dialogTitle: configuratorPageT.selectProduct,
          component: <SelectProduct onSave={handleSave} />,
          saveAndCancelButton: {
            saveDisabled: !formData.selectedProductId,
            saveLabel: "selectProductAndSave",
            saveFn() {
              handleSave();
            },
            cancelLabel: formData.productsFilter?.parentProjectLineItemId ? "cancel" : "back",
            cancelFn() {
              if (formData.productsFilter?.parentProjectLineItemId) {
                setAddEditConfigurationDialog(undefined);
              } else {
                setAddEditConfigurationDialog({ step: "SELECT SYSTEM" });
              }
            }
          }
        };
      case "PRODUCT-DETAILS":
        return {
          dialogTitle: configuratorPageT.selectProduct,
          component: <ProductDetails />,
          saveAndCancelButton: {
            saveDisabled: false,
            saveLabel: "selectProductAndSave",
            saveFn() {
              handleSave();
            },
            cancelLabel: "back",
            cancelFn() {
              setAddEditConfigurationDialog({ step: "SELECT PRODUCT" });
            }
          },
          onClose: () => setAddEditConfigurationDialog({ step: "SELECT PRODUCT" })
        };
    }
  }, [addEditConfigurationDialog?.step, configuratorPageT, formData]);

  if (!renderStep) return null;

  return (
    <GenericDialog
      dialogProps={{
        maxWidth: "lg"
      }}
      dialogContentProps={{
        sx: {
          height: "80vh"
        }
      }}
      headerTitle={renderStep.dialogTitle}
      onClose={() => {
        if (isLoading) return;
        renderStep.onClose ? renderStep.onClose() : setAddEditConfigurationDialog(undefined);
      }}
      renderDialogActions={
        <>
          <TooltipShortcut shortcut={{ action: renderStep.saveAndCancelButton.cancelFn, shortCutKey: "a", modifier: "alt" }}>
            <Button
              disabled={isLoading}
              t={genericTranslationsT}
              k={genericTranslationsK}
              field={renderStep.saveAndCancelButton.cancelLabel}
              onClick={renderStep.saveAndCancelButton.cancelFn}
              variant="outlined"
            />
          </TooltipShortcut>
          <TooltipShortcut shortcut={{ action: renderStep.saveAndCancelButton.saveFn, shortCutKey: "g", modifier: "alt" }}>
            <Button
              t={configuratorPageT}
              k={configuratorPageK}
              field={renderStep.saveAndCancelButton.saveLabel}
              onClick={renderStep.saveAndCancelButton.saveFn}
              variant="contained"
              disabled={renderStep.saveAndCancelButton.saveDisabled || isLoading}
            />
          </TooltipShortcut>
        </>
      }
    >
      {renderStep.component}
    </GenericDialog>
  );
};

export const AddEditConfigurationDialog = connect(mapStateToProps, ownActions)(AddEditConfigurationDialogBase);
